import React, { useEffect, useState } from "react";
import Navbar from "@/components/Navbar";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { api } from "@/lib/api";
import { Bar, BarChart, ResponsiveContainer, Tooltip, XAxis, YAxis, CartesianGrid, Legend } from "recharts";
import { Users, FileText, Lock, TrendingUp, Mail, Loader2, ShieldAlert } from "lucide-react";

function Stat({ label, value, sub, icon: Icon, testId, tone = "default" }) {
  const toneMap = {
    default: "bg-slate-100 text-slate-700",
    emerald: "bg-emerald-100 text-emerald-700",
    amber: "bg-amber-100 text-amber-700",
    blue: "bg-blue-100 text-blue-700",
  };
  return (
    <Card className="bg-white border border-gray-200 rounded-2xl" data-testid={testId}>
      <CardContent className="p-6">
        <div className={`h-10 w-10 rounded-xl flex items-center justify-center ${toneMap[tone]}`}>
          <Icon className="h-5 w-5" />
        </div>
        <p className="mt-5 text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">{label}</p>
        <p className="mt-2 text-3xl font-medium tracking-tight text-slate-900">{value}</p>
        {sub && <p className="mt-1 text-xs text-slate-500">{sub}</p>}
      </CardContent>
    </Card>
  );
}

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/stats");
        setStats(data);
      } catch (e) {
        setErr(e.response?.status === 403 ? "forbidden" : "error");
      }
    })();
  }, []);

  if (err === "forbidden") {
    return (
      <div className="min-h-screen bg-[#FAFAFA]">
        <Navbar />
        <main className="max-w-lg mx-auto px-6 py-24 text-center">
          <div className="mx-auto h-14 w-14 rounded-full bg-rose-100 text-rose-700 flex items-center justify-center mb-4">
            <ShieldAlert className="h-7 w-7" />
          </div>
          <h1 className="text-2xl font-medium tracking-tight" data-testid="admin-forbidden">Kun for administrator</h1>
          <p className="mt-3 text-slate-500">Du har ikke tilgang til denne siden.</p>
        </main>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="min-h-screen bg-[#FAFAFA]">
        <Navbar />
        <div className="flex items-center justify-center py-24"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
      </div>
    );
  }

  const fmt = (n) => new Intl.NumberFormat("nb-NO").format(Math.round(n));
  const chartData = stats.daily_last_30.map((d) => ({
    ...d,
    label: d.date.slice(5), // MM-DD
  }));

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Navbar />
      <main className="max-w-6xl mx-auto px-6 lg:px-8 py-12">
        <div className="flex items-baseline justify-between flex-wrap gap-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Admin</p>
            <h1 className="mt-2 text-4xl font-medium tracking-tight text-slate-900" data-testid="admin-title">Statistikk</h1>
            <p className="mt-2 text-slate-600">Oversikt over IdeSetGo - siste 30 dager.</p>
          </div>
          <Badge variant="secondary" className="rounded-full">Kun for eier</Badge>
        </div>

        <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Stat testId="stat-users" label="Brukere" value={fmt(stats.users.total)} sub={`${fmt(stats.users.paying)} betalende`} icon={Users} tone="blue" />
          <Stat testId="stat-plans" label="Planer" value={fmt(stats.plans.total)} sub={`${fmt(stats.plans.generated)} generert`} icon={FileText} tone="default" />
          <Stat testId="stat-unlocked" label="Opplåste planer" value={fmt(stats.plans.unlocked)} sub={`${stats.plans.conv_rate}% konvertering`} icon={Lock} tone="emerald" />
          <Stat testId="stat-revenue" label="Omsetning" value={`${fmt(stats.revenue.total_nok)} kr`} sub={`ARPU: ${fmt(stats.revenue.arpu)} kr`} icon={TrendingUp} tone="amber" />
        </div>

        <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Stat testId="stat-newsletter" label="Nyhetsbrev" value={fmt(stats.newsletter.total)} sub="Påmeldte totalt" icon={Mail} tone="default" />
          <Stat testId="stat-user-conv" label="Bruker-konvertering" value={`${stats.users.conv_rate}%`} sub="Betalende / totale brukere" icon={TrendingUp} tone="emerald" />
          <Stat testId="stat-plan-conv" label="Plan-konvertering" value={`${stats.plans.conv_rate}%`} sub="Opplåst / totalt antall planer" icon={TrendingUp} tone="blue" />
        </div>

        <Card className="mt-10 bg-white border border-gray-200 rounded-2xl">
          <CardContent className="p-6 sm:p-8">
            <div className="flex items-baseline justify-between mb-4 flex-wrap gap-3">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Aktivitet siste 30 dager</p>
                <h2 className="mt-1 text-xl font-medium tracking-tight text-slate-900">Planer opprettet vs. opplåst</h2>
              </div>
              <div className="flex gap-4 text-xs">
                <span className="inline-flex items-center gap-1.5 text-slate-600"><span className="h-2 w-2 rounded-full bg-slate-900"></span> Opprettet</span>
                <span className="inline-flex items-center gap-1.5 text-slate-600"><span className="h-2 w-2 rounded-full bg-emerald-500"></span> Opplåst</span>
              </div>
            </div>
            <div className="h-72" data-testid="admin-chart">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <CartesianGrid vertical={false} strokeDasharray="3 3" stroke="#E5E7EB" />
                  <XAxis dataKey="label" axisLine={false} tickLine={false} tick={{ fill: "#94A3B8", fontSize: 10 }} interval={4} />
                  <YAxis axisLine={false} tickLine={false} tick={{ fill: "#94A3B8", fontSize: 10 }} allowDecimals={false} />
                  <Tooltip cursor={{ fill: "rgba(15,23,42,0.04)" }} contentStyle={{ background: "#0F172A", border: "none", borderRadius: 8, color: "#F8FAFC", fontSize: 12 }} labelStyle={{ color: "#94A3B8" }} />
                  <Legend content={() => null} />
                  <Bar dataKey="plans" fill="#0F172A" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="unlocked" fill="#10B981" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
